import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Search,
  FilterList,
  Group,
  CalendarToday,
  Person,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ToolboxTalk {
  _id: string;
  title: string;
  topic: string;
  description: string;
  date: string;
  duration: number; // in minutes
  conductor: {
    firstName: string;
    lastName: string;
  };
  attendees: number;
  project: {
    name: string;
  };
  status: 'scheduled' | 'completed' | 'cancelled';
  safetyTopics: string[];
  keyPoints: string[];
  notes?: string;
}

const ToolboxTalks: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [talks, setTalks] = useState<ToolboxTalk[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTalk, setSelectedTalk] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const getPrimaryTopic = (t: any): string => {
      if (typeof t?.topic === 'string') return t.topic;
      if (t?.topic?.primary) return t.topic.primary;
      return typeof t?.title === 'string' ? t.title : 'Toolbox Talk';
    };

    const mapTalk = (t: any): ToolboxTalk => ({
      _id: t._id || t.id || '',
      title: typeof t.title === 'string' ? t.title : getPrimaryTopic(t) || 'Untitled Talk',
      topic: getPrimaryTopic(t) || t.category || 'General Safety',
      description: t.description || '',
      date: (t.date || t.scheduledDate || new Date().toISOString().split('T')[0]).toString().slice(0, 10),
      duration: Number(t.duration || 0),
      conductor: {
        firstName: t.conductor?.firstName || t.conductorFirstName || t.conductor?.name?.split(' ')?.[0] || 'Unknown',
        lastName: t.conductor?.lastName || t.conductorLastName || t.conductor?.name?.split(' ')?.[1] || '',
      },
      attendees: Array.isArray(t.attendees) ? t.attendees.length : Number(t.attendeesCount || 0),
      project: {
        name: t.project?.name || t.projectName || 'Unassigned',
      },
      status: (t.status || 'scheduled') as ToolboxTalk['status'],
      safetyTopics: t.safetyTopics || t.topics?.map((x: any) => x.title) || [],
      keyPoints: Array.isArray(t.keyPoints)
        ? t.keyPoints.map((kp: any) => (typeof kp === 'string' ? kp : kp.point || ''))
        : [],
      notes: t.notes || undefined,
    });

    const fetchTalks = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/toolbox-talks`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch toolbox talks');
        const data = await res.json();
        const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setTalks(items.map(mapTalk));
      } catch (e: any) {
        console.error('Error fetching toolbox talks:', e);
        setError(e?.message || 'Error fetching toolbox talks');
        setTalks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTalks();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, talkId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedTalk(talkId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTalk(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle />;
      case 'scheduled':
        return <Schedule />;
      default:
        return <Schedule />;
    }
  };

  const filteredTalks = talks.filter((talk) => {
    const q = (searchTerm || '').toLowerCase();
    const title = (talk.title || '').toString().toLowerCase();
    const topic = (talk.topic || '').toString().toLowerCase();
    const first = (talk.conductor?.firstName || '').toLowerCase();
    const last = (talk.conductor?.lastName || '').toLowerCase();
    return title.includes(q) || topic.includes(q) || first.includes(q) || last.includes(q);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Toolbox Talks
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigate('/toolbox-talks/create')}
        >
          Schedule Talk
        </Button>
      </Box>

      {error && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography color="error">{error}</Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search toolbox talks..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ borderRadius: 2 }}
            >
              Filter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {filteredTalks.map((talk) => (
          <Card 
            key={talk._id} 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate(`/toolbox-talks/${talk._id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: getStatusColor(talk.status) + '.main' }}>
                    {getStatusIcon(talk.status)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {talk.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {talk.topic}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={talk.status.charAt(0).toUpperCase() + talk.status.slice(1)}
                    color={getStatusColor(talk.status) as any}
                    size="small"
                  />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, talk._id);
                    }}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body1" sx={{ mb: 2 }}>
                {talk.description}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(talk.date).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {talk.duration} minutes
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {talk.conductor.firstName} {talk.conductor.lastName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Group fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {talk.attendees} attendees
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Safety Topics:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {talk.safetyTopics.map((topic, index) => (
                    <Chip
                      key={index}
                      label={topic}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Key Points:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {talk.keyPoints.map((point, index) => (
                    <Typography key={index} component="li" variant="body2" color="text.secondary">
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {talk.notes && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {talk.notes}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit Talk</MenuItem>
        <MenuItem onClick={handleMenuClose}>Mark Complete</MenuItem>
        <MenuItem onClick={handleMenuClose}>Download Attendance</MenuItem>
        <MenuItem onClick={handleMenuClose}>Reschedule</MenuItem>
        <MenuItem onClick={handleMenuClose}>Cancel</MenuItem>
      </Menu>
    </Box>
  );
};

export default ToolboxTalks;